import axios from "axios";
import config from "../config";

const api = axios.create({
  baseURL: config.API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// â”€â”€ Request interceptor â€” attach access token to every request â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
api.interceptors.request.use(
  (reqConfig) => {
    const token = localStorage.getItem("admin_token");
    if (token) reqConfig.headers["Authorization"] = `Bearer ${token}`;
    if (config.API_KEY) reqConfig.headers["api-key"] = config.API_KEY;
    return reqConfig;
  },
  (error) => Promise.reject(error)
);

// â”€â”€ Response interceptor â€” silent token refresh on 401 â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

const clearAuthAndRedirect = () => {
  localStorage.removeItem("admin_token");
  localStorage.removeItem("admin_refresh_token");
  localStorage.removeItem("admin_user");
  sessionStorage.removeItem("admin_session_only");
  window.location.href = "/login?expired=1";
};

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config || {};

    if (
      err.response?.status !== 401 ||
      original._retry ||
      original._isRefreshCall ||
      original._isSetupVerifyCall
    ) {
      return Promise.reject(err);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          original.headers = original.headers || {};
          original.headers["Authorization"] = `Bearer ${token}`;
          return api(original);
        })
        .catch((e) => Promise.reject(e));
    }

    original._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem("admin_refresh_token");

    if (!refreshToken) {
      isRefreshing = false;
      clearAuthAndRedirect();
      return Promise.reject(err);
    }

    try {
      const { data } = await axios.post(
        `${config.API_BASE_URL}/admin/auth/refresh`,
        { refreshToken },
        {
          _isRefreshCall: true,
          headers: {
            "Content-Type": "application/json",
            ...(config.API_KEY ? { "api-key": config.API_KEY } : {}),
          },
        }
      );

      const newAccessToken = data.data.token;
      const newRefreshToken = data.data.refreshToken;

      localStorage.setItem("admin_token", newAccessToken);
      localStorage.setItem("admin_refresh_token", newRefreshToken);

      api.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
      processQueue(null, newAccessToken);

      original.headers = original.headers || {};
      original.headers["Authorization"] = `Bearer ${newAccessToken}`;
      return api(original);
    } catch (refreshErr) {
      processQueue(refreshErr, null);
      clearAuthAndRedirect();
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

// â”€â”€ Session-only logout â€” fires only on true tab/window CLOSE, not F5/refresh â”€
// â”€â”€ Auth â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const authApi = {
  login: (data) => api.post("/admin/auth/login", data),

  logout: () =>
    api.post("/admin/auth/logout", {
      refreshToken: localStorage.getItem("admin_refresh_token"),
    }),

  refresh: (refreshToken) =>
    axios.post(
      `${config.API_BASE_URL}/admin/auth/refresh`,
      { refreshToken },
      {
        _isRefreshCall: true,
        headers: {
          "Content-Type": "application/json",
          ...(config.API_KEY ? { "api-key": config.API_KEY } : {}),
        },
      }
    ),

  me: () => api.get("/admin/auth/me"),

  changePassword: (data) => api.post("/admin/auth/change-password", data),

  setup2FA: () => api.post("/admin/auth/2fa/setup"),

  verify2FA: (code) => api.post("/admin/auth/2fa/verify", { code }),

  verifySetup2FA: (code, setupToken) =>
    axios.post(
      `${config.API_BASE_URL}/admin/auth/2fa/verify?fromLogin=true`,
      { code, setupToken },   // â† both in body so backend can read req.getSetupToken()
      {
        _isSetupVerifyCall: true,
        headers: {
          "Content-Type": "application/json",
          ...(config.API_KEY ? { "api-key": config.API_KEY } : {}),
        },
      }
    ),

  disable2FA: () => api.post("/admin/auth/2fa/disable"),

  reset2FA: () => api.post("/admin/auth/2fa/reset"),
};

// â”€â”€ Dashboard â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const dashboardApi = {
  getStats: (startDate, endDate, filter = "ALL_TIME") => {
    const params = { filter };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get("/admin/dashboard/stats", { params });
  },
};

// â”€â”€ Admin Users â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const adminUsersApi = {
  getAll: (
    page = 0,
    size = 20,
    search = "",
    sort = "createdAt",
    dir = "DESC",
    active = null,
    locked = null,
    roleId = null,
    twoFaEnabled = null
  ) => {
    const params = { page, size, sort, dir };
    if (search) params.search = search;
    if (active !== null) params.active = active;
    if (locked !== null) params.locked = locked;
    if (roleId) params.roleId = roleId;
    if (twoFaEnabled !== null) params.twoFaEnabled = twoFaEnabled;
    return api.get("/admin/admins", { params });
  },
  getById: (id) => api.get(`/admin/admins/${id}`),
  create: (data) => api.post("/admin/admins", data),
  update: (id, data) => api.put(`/admin/admins/${id}`, data),
  updateStatus: (id, active) =>
    api.put(`/admin/admins/${id}/status`, null, { params: { active } }),
  updateLock: (id, locked) =>
    api.put(`/admin/admins/${id}/lock`, null, { params: { locked } }),
  resetPassword: (id, newPassword) =>
    api.post(`/admin/admins/${id}/reset-password`, { newPassword }),
  delete: (id) => api.delete(`/admin/admins/${id}`),
  getRoleMenu: (id) => api.get(`/admin/admins/${id}/role-menu`),
  updateRoleMenu: (id, menuIds) =>
    api.put(`/admin/admins/${id}/role-menu`, { menuIds }),
  getPolicy: (id) => api.get(`/admin/admins/${id}/security-policy`),
  updatePolicy: (id, data) =>
    api.put(`/admin/admins/${id}/security-policy`, data),
};

// â”€â”€ End Users â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const usersApi = {
  getAll: (
    search = "",
    page = 0,
    size = 20,
    sort = "createdAt",
    dir = "desc",
    active = null,
    gender = null,
    source = null,
    deviceType = null
  ) => {
    const params = { page, size, sort, dir };
    if (search) params.search = search;
    if (active !== null) params.active = active;
    if (gender) params.gender = gender;
    if (source) params.source = source;
    if (deviceType) params.deviceType = deviceType;
    return api.get("/admin/users", { params });
  },
  getStats: () => api.get("/admin/users/stats"),
  getById: (id) => api.get(`/admin/users/${id}`),
  toggleStatus: (id, active) =>
    api.put(`/admin/users/${id}/status`, null, { params: { active } }),
  delete: (id) => api.delete(`/admin/users/${id}`),
  getWallet: (id) => api.get(`/admin/users/${id}/wallet`),
  getTransactions: (id, page = 0, size = 20) =>
    api.get(`/admin/users/${id}/transactions`, { params: { page, size } }),
  getKundli: (id) => api.get(`/admin/users/${id}/kundli`),
  getCompatibility: (id) => api.get(`/admin/users/${id}/compatibility`),
  getHoroscope: (id) => api.get(`/admin/users/${id}/horoscope`),
  getSupportQueries: (id) => api.get(`/admin/users/${id}/support-queries`),
  updateSupportQueryStatus: (queryId, status) =>
    api.patch(`/admin/support-queries/${queryId}/status`, null, {
      params: { status },
    }),
};

// â”€â”€ Wallets â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const walletsApi = {
  getStats: () => api.get("/admin/wallets/stats"),
  getAllTransactions: (page = 0, size = 20, transactionType = null) =>
    api.get("/admin/wallets/transactions", {
      params: {
        page,
        size,
        sort: "createdAt",
        dir: "desc",
        ...(transactionType ? { transactionType } : {}),
      },
    }),
};

// â”€â”€ Credits â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const creditsApi = {
  getPacks: (
    search = "",
    active = null,
    popular = null,
    packType = null,
    page = 0,
    size = 50,
    sort = "sortOrder",
    dir = "asc"
  ) => {
    const params = { page, size, sort, dir };
    if (search) params.search = search;
    if (active !== null) params.active = active;
    if (popular !== null) params.popular = popular;
    if (packType !== null) params.packType = packType;
    return api.get("/admin/credits/packs", { params });
  },
  getPackById: (id) => api.get(`/admin/credits/packs/${id}`),
  createPack: (data) => api.post("/admin/credits/packs", data),
  updatePack: (id, data) => api.put(`/admin/credits/packs/${id}`, data),
  togglePack: (id, active) =>
    api.patch(`/admin/credits/packs/${id}/toggle`, null, {
      params: { active },
    }),
  deletePack: (id) => api.delete(`/admin/credits/packs/${id}`),
  getOrders: (
  status   = null,
  search   = "",
  packType = null,
  page     = 0,
  fromDate = null,
  toDate   = null,
  size     = 20,
  sort     = "createdAt",
  dir      = "desc"
) => {
  const params = { page, size, sort, dir };
  if (status)   params.status   = status;
  if (search)   params.search   = search;
  if (packType) params.packType = packType;
  if (fromDate) params.fromDate = fromDate;
  if (toDate)   params.toDate   = toDate;
  return api.get("/admin/credits/orders", { params });
},
  getOrderStats: () => api.get("/admin/credits/orders/stats"),
  getOrderById: (id) => api.get(`/admin/credits/orders/${id}`),
  getOrdersByUser: (userId, page = 0, size = 20) =>
    api.get(`/admin/credits/orders/user/${userId}`, {
      params: { page, size },
    }),
  deleteOrder: (id) => api.delete(`/admin/credits/orders/${id}`),
};

// â”€â”€ Services â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const serviceApi = {
  getServices: (tier = null, active = null, search = "") => {
    const params = {};
    if (tier) params.tier = tier;
    if (active !== null) params.active = active;
    if (search) params.search = search;
    return api.get("/admin/services", { params });
  },
  getServiceById: (id) => api.get(`/admin/services/${id}`),
  createService: (data) => api.post("/admin/services", data),
  updateService: (id, data) => api.put(`/admin/services/${id}`, data),
  toggleService: (id, active) =>
    api.patch(`/admin/services/${id}/toggle`, { active }),
  updateCredit: (id, credit) =>
    api.patch(`/admin/services/${id}/credit`, { credit }),
  toggleSubService: (id, data) =>
    api.patch(`/admin/services/${id}/sub-services/toggle`, data),
  reorderServices: (ids) => api.patch("/admin/services/reorder", ids),
  uploadServiceIcon: (id, formData) =>
    api.post(`/admin/services/${id}/icon`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  deleteServiceIcon: (id) => api.delete(`/admin/services/${id}/icon`),
  deleteService: (id) => api.delete(`/admin/services/${id}`),
  getServicesWithTranslations: (languageId = null) => {
    const params = {};
    if (languageId) params.languageId = languageId;
    return api.get("/admin/services/translations", { params });
  },
  getServiceTranslations: (id) => api.get(`/admin/services/${id}/translations`),
  upsertServiceTranslation: (id, data) =>
    api.put(`/admin/services/${id}/translations`, data),
};

// â”€â”€ Zodiacs / Horoscopes â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const zodiacsApi = {
  getAll: (languageId = null) =>
    api.get("/admin/zodiacs", { params: languageId ? { languageId } : {} }),
  getById: (id) => api.get(`/admin/zodiacs/${id}`),
  upsertTranslation: (id, body) => api.put(`/admin/zodiacs/${id}/translations`, body),
  updateImages: (id, formData) =>
    api.put(`/admin/zodiacs/${id}/images`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  getHoroscopes: (
    periodType = "",
    page = 0,
    search = "",
    language = "",
    zodiacId = "",
    mood = "",
    size = 20
  ) => {
    const params = { page, size };
    if (periodType) params.periodType = periodType;
    if (search) params.search = search;
    if (language) params.languageId = language;
    if (zodiacId) params.zodiacId = zodiacId;
    if (mood) params.mood = mood;
    return api.get("/admin/zodiacs/horoscopes", { params });
  },
  getHoroscopeByZodiac: (zodiacId, periodType = "DAILY") =>
    api.get(`/admin/zodiacs/${zodiacId}/horoscopes`, { params: { periodType } }),
};

// â”€â”€ Kundli â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const kundliApi = {
  getAll: (page = 0, size = 20) => api.get("/admin/kundli", { params: { page, size } }),
  delete: (id) => api.delete(`/admin/kundli/${id}`),
};

// â”€â”€ Menu â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const menuApi = {
  getMyMenu: () => api.get("/admin/menu/my-menu"),
  getAll: () => api.get("/admin/menu"),
  create: (data) => api.post("/admin/menu", data),
  update: (id, data) => api.put(`/admin/menu/${id}`, data),
  delete: (id) => api.delete(`/admin/menu/${id}`),
  addRole: (menuId, role) => api.post(`/admin/menu/${menuId}/roles/${role}`),
  removeRole: (menuId, role) => api.delete(`/admin/menu/${menuId}/roles/${role}`),
};

// â”€â”€ Security Policy â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const securityApi = {
  getAll: () => api.get("/admin/security-policy"),
  getByRole: (role) => api.get(`/admin/security-policy/role/${role}`),
  upsert: (data) => api.post("/admin/security-policy", data),
  delete: (id) => api.delete(`/admin/security-policy/${id}`),
};

// â”€â”€ Audit Logs â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const auditApi = {
  getAll: (page = 0, search = "", action = "", entityType = "", size = 20) => {
    const params = { page, size };
    if (search) params.search = search;
    if (action) params.action = action;
    if (entityType) params.entityType = entityType;
    return api.get("/admin/audit-logs", { params });
  },
  getRecent: () => api.get("/admin/audit-logs/recent"),
  getByUser: (username) => api.get(`/admin/audit-logs/user/${username}`),
  getByAction: (action) => api.get(`/admin/audit-logs/action/${action}`),
  getByDateRange: (start, end) =>
    api.get("/admin/audit-logs/date-range", { params: { start, end } }),
};

// â”€â”€ Reports â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const reportsApi = {
  requestReport: (req) => api.post("/admin/reports/request", req),
  getJob: (jobId) => api.get(`/admin/reports/jobs/${jobId}`),
  getMyJobs: () => api.get("/admin/reports/my-jobs"),
  getAllJobs: (page = 0, size = 20) =>
    api.get("/admin/reports/all-jobs", { params: { page, size } }),
  deleteJob: (jobId) => api.delete(`/admin/reports/jobs/${jobId}`),
};

// â”€â”€ Banners â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function buildBannerFormData(body, imageFile) {
  const fd = new FormData();
  const { image, ...rest } = body;
  if (imageFile instanceof File) {
    fd.append("data", JSON.stringify(rest));
    fd.append("image", imageFile);
  } else if (image) {
    fd.append("data", JSON.stringify({ ...rest, image }));
  } else {
    fd.append("data", JSON.stringify(rest));
  }
  return fd;
}

export const bannersApi = {
  getAll: (type) => api.get("/admin/banners", { params: type ? { type } : {} }),
  getById: (id) => api.get(`/admin/banners/${id}`),
  create: (body, imageFile) =>
    api.post("/admin/banners", buildBannerFormData(body, imageFile), {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  update: (id, body, imageFile) =>
    api.put(`/admin/banners/${id}`, buildBannerFormData(body, imageFile), {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  toggle: (id, active) =>
    api.patch(`/admin/banners/${id}/toggle`, null, { params: { active } }),
  delete: (id) => api.delete(`/admin/banners/${id}`),
};

// â”€â”€ Legal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const legalApi = {
  getAll: () => api.get("/admin/legal"),
  getByType: (type) => api.get(`/admin/legal/${type}`),
  upsert: (body) => api.post("/admin/legal", body),
};

// â”€â”€ FAQ â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const faqApi = {
  getCategories: () => api.get("/admin/legal/faq/categories"),
  createCategory: (body) => api.post("/admin/legal/faq/categories", body),
  updateCategory: (id, body) => api.put(`/admin/legal/faq/categories/${id}`, body),
  deleteCategory: (id) => api.delete(`/admin/legal/faq/categories/${id}`),
  getItems: (categoryId) =>
    api.get("/admin/legal/faq/items", { params: categoryId ? { categoryId } : {} }),
  createItem: (body) => api.post("/admin/legal/faq/items", body),
  updateItem: (id, body) => api.put(`/admin/legal/faq/items/${id}`, body),
  deleteItem: (id) => api.delete(`/admin/legal/faq/items/${id}`),
  getCategoriesByLanguage: (languageId) =>
    api.get(`/admin/legal/faq/categories/by-language/${languageId}`),
  getLanguages: () => api.get("/admin/languages"),
};

// â”€â”€ Support Queries â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const supportQueriesApi = {
  getAll: (page = 0, size = 20, search = "", status = "", sort = "createdAt", dir = "desc") =>
    api.get("/admin/support-queries", {
      params: { page, size, ...(search && { search }), ...(status && { status }), sort, dir },
    }),
  getById: (id) => api.get(`/admin/support-queries/${id}`),
  getByUser: (userId) => api.get(`/admin/support-queries/user/${userId}`),
  updateStatus: (id, status) =>
    api.patch(`/admin/support-queries/${id}/status`, null, { params: { status } }),
  delete: (id) => api.delete(`/admin/support-queries/${id}`),
};

// â”€â”€ Contact Support Info â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const contactSupportApi = {
  // Contact info (singleton)
  get:    ()       => api.get("/admin/contact-support-info"),
  update: (data)   => api.put("/admin/contact-support-info", data),
  toggle: (active) => api.patch("/admin/contact-support-info/toggle", null, { params: { active } }),
  getSocialLinks:   ()              => api.get("/admin/contact-support-info/social-links"),
  getSocialLinkById:(id)            => api.get(`/admin/contact-support-info/social-links/${id}`),
  upsertSocialLink: (data)          => api.put("/admin/contact-support-info/social-links", data),
  toggleSocialLink: (id, active)    => api.patch(`/admin/contact-support-info/social-links/${id}/toggle`, null, { params: { active } }),
};

// â”€â”€ App Versions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const appVersionsApi = {
  getAll: () => api.get("/admin/app-versions"),
  getByPlatform: (platform) => api.get(`/admin/app-versions/platform/${platform}`),
  create: (data) => api.post("/admin/app-versions", data),
  update: (id, data) => api.put(`/admin/app-versions/${id}`, data),
  toggleForce: (id, val) =>
    api.patch(`/admin/app-versions/${id}/force-update`, null, {
      params: { forceUpdate: val },
    }),
  toggleMaintenance: (id, val) =>
    api.patch(`/admin/app-versions/${id}/maintenance`, null, {
      params: { maintenance: val },
    }),
  toggleSoftUpdate: (id, val) =>
    api.patch(`/admin/app-versions/${id}/soft-update`, null, {
      params: { softUpdate: val },
    }),
  delete: (id) => api.delete(`/admin/app-versions/${id}`),
};

export const deletedUsersApi = {
  getAll: (params) => api.get("/admin/users/deleted", { params }),
};

export default api;
